import { isObject, keyBy } from 'lodash-es'
import { AdUnit } from './adunit'
import { HistorizedScopedModel, HistorizedScopedModelFieldNames } from './base-models'
// Type-only, deliberately: `bidder-seat` imports MAPPING_DIMENSION_KEYS from here at runtime, so a value
// import back the other way closes a cycle — and the loser is whichever module the entry point reaches
// second, which gets `undefined` where it expected the constant. Anything seat-aware enough to need a
// value from `bidder-seat` belongs over there instead; see `getPublisherBidderSeatView`.
import type { BidderSeat } from './bidder-seat'
import { DeviceType } from './device-type'
import { MediaType } from './media-types'
import { z } from 'zod/v4'

export const PBSTCK_BIDDER_DEFAULT_BID_PARAMS = {
  isEnabled: 'x',
}

export const MAPPING_DIMENSIONS = {
  scopeId: 'Scope id',
  siteId: 'Site name',
  adUnitId: 'Ad unit name',
  mediaType: 'Media type',
  placementId: 'Placement',
  device: 'Device',
} as const

export const MAPPING_DIMENSION_KEYS = Object.keys(MAPPING_DIMENSIONS) as readonly MappingDimensionKey[]
export type MappingDimensionKey = keyof typeof MAPPING_DIMENSIONS

/**
 * What identifies a mapping within its seat: which dimensions it carries and their values.
 *
 * The names are part of the key because seats carry different shapes (`{placementId}`, `{siteId, adUnitId}`,
 * `{siteId, adUnitId, mediaType}`…), and joined values alone cannot tell them apart. The canonical
 * MAPPING_DIMENSION_KEYS order keeps the key independent of property insertion order, which differs between
 * freshly generated combos and mappings persisted in Mongo. A null value counts as absent.
 */
export const mappingDimensionsKey = (dimensions: Partial<Record<MappingDimensionKey, string | null>>): string =>
  MAPPING_DIMENSION_KEYS.filter((key) => dimensions[key] != null)
    .map((key) => `${key}=${dimensions[key]}`)
    .join('|')

export const MAPPING_DIMENSION_LABELS = Object.values(MAPPING_DIMENSIONS) as readonly MappingDimensionLabel[]
export type MappingDimensionLabel = (typeof MAPPING_DIMENSIONS)[MappingDimensionKey]

export type BidParamsMappingDimensions = {
  scopeId?: string
  siteId?: string
  adUnitId?: string
  mediaType?: MediaType
  placementId?: string
  device?: DeviceType
}
export type MappingDimensionsConfig = { [K in keyof BidParamsMappingDimensions]: { values: string[]; labels?: Record<string, string> } }

export type BidParams = { [key: string]: string | number | boolean | string[] | BidParams }

// Common object for ADM and PBS
export interface BidParamsMapping extends HistorizedScopedModel {
  bidderSeatId: string
  bidParams: BidParams
  dimensions: BidParamsMappingDimensions
  enabled: boolean
}

export type CreateBidParamsMapping = Omit<BidParamsMapping, HistorizedScopedModelFieldNames>

export type DimensionValues = {
  siteIds: string[]
  adUnitIds: string[]
  devices?: string[]
  mediaTypes?: string[]
}

export const BIDDER_PARAM_PROPERTIES = {
  'Pbstck Adapter Name': 'bidderName',
  'Pbstck Site': 'siteName',
  'Pbstck Device': 'device',
  'Pbstck Ad Unit': 'adUnitName',
} as const

export const BIDDER_PARAM_CSV_HEADERS: (keyof typeof BIDDER_PARAM_PROPERTIES)[] = ['Pbstck Adapter Name', 'Pbstck Site', 'Pbstck Device', 'Pbstck Ad Unit']

export const isBidderParamProperty = (value: string): value is keyof typeof BIDDER_PARAM_PROPERTIES => {
  return Object.keys(BIDDER_PARAM_PROPERTIES).includes(value)
}

// This function generates all possible mappings based on the provided dimension values and existing mappings.
// It creates a Cartesian product of the dimension values and fills in the existing mappings if they match.
export const generateMappings = ({
  existingMappings,
  siteAdUnitCouplesToRestrictTo,
  siteAdUnitCouplesToExclude,
  mappingDimensionsConfig,
  bidderSeatId,
  adUnits,
}: {
  existingMappings: BidParamsMapping[]
  siteAdUnitCouplesToRestrictTo?: Set<`${string}-${string}`>
  siteAdUnitCouplesToExclude: Set<`${string}-${string}`>
  mappingDimensionsConfig: MappingDimensionsConfig
  bidderSeatId: string
  adUnits?: Pick<AdUnit, 'id' | 'devices'>[] // only used for One
}): (BidParamsMapping | CreateBidParamsMapping)[] => {
  // Filter dimension values that are not empty

  const entries = Object.entries(mappingDimensionsConfig).filter(([_, v]) => v.values.length > 0)
  // Abort if no dimension values
  if (!entries.length) return []
  const existingMappingsByDimensions = keyBy(existingMappings, (mapping) => mappingDimensionsKey(mapping.dimensions))
  // Cartesian product of all dimension values
  const dimensionsCombos = entries.reduce<Record<string, string>[]>(
    (acc, [k, v]) => {
      const result: Record<string, string>[] = []

      for (const item of acc) {
        for (const value of v.values) {
          result.push({ ...item, [k]: value })
        }
      }

      return result
    },
    [{}]
  )
  // Fill with existing mappings
  return dimensionsCombos
    .filter((e) => {
      // Only filter if both siteId and adUnitId exist in the dimension combo
      if (e.siteId !== undefined && e.adUnitId !== undefined) {
        return !siteAdUnitCouplesToExclude.has(`${e.siteId}-${e.adUnitId}`)
      }
      return true
    })
    .filter((e) => {
      // Only filter if both siteId and adUnitId exist in the dimension combo
      if (!siteAdUnitCouplesToRestrictTo) return true
      if (e.siteId !== undefined && e.adUnitId !== undefined) {
        return siteAdUnitCouplesToRestrictTo.has(`${e.siteId}-${e.adUnitId}`)
      }
      return true
    })
    .filter((e) => {
      // ONE only -- We must remove rows if ad unit is not compatible with a device
      if (!e.device || !adUnits) return true
      const { adUnitId, device } = e
      const adUnit = adUnits?.find((a) => a.id === adUnitId)
      return adUnit && adUnit.devices.includes(device as DeviceType)
    })
    .map((dimensionCombo) => {
      const lookupKey = mappingDimensionsKey(dimensionCombo)
      const existingMapping = existingMappingsByDimensions[lookupKey]
      return existingMapping
        ? {
            ...existingMapping,
            bidParams: explodeParams(existingMapping.bidParams),
          }
        : ({
            dimensions: {
              ...(mappingDimensionsConfig.scopeId ? { scopeId: dimensionCombo.scopeId } : {}),
              ...(mappingDimensionsConfig.siteId ? { siteId: dimensionCombo.siteId } : {}),
              ...(mappingDimensionsConfig.adUnitId ? { adUnitId: dimensionCombo.adUnitId } : {}),
              ...(mappingDimensionsConfig.placementId ? { placementId: dimensionCombo.placementId } : {}),
              ...(mappingDimensionsConfig.mediaType ? { mediaType: dimensionCombo.mediaType as MediaType } : {}),
              ...(mappingDimensionsConfig.device ? { device: dimensionCombo.device as DeviceType } : {}),
            },
            bidderSeatId,
            bidParams: {},
          } as CreateBidParamsMapping)
    })
}

export const explodeParams = (bidderParamsValue: BidParams | undefined): BidParams => {
  // explode object properties to have a flat object with property paths as keys
  // { publisher: {id: '123', domain:'lala'} } => { 'publisher.id': '123', 'publisher.domain': 'lala' }
  const explodedParams: BidParams = {}
  const explode = (obj: BidParams, path: string = '') => {
    for (const key in obj) {
      const value = obj[key]
      const propertyPath = path ? `${path}.${key}` : key
      if (!isObject(value)) {
        explodedParams[propertyPath] = value
      } else if (Array.isArray(value)) {
        // output array formated string between quotes to be CSV compliant
        explodedParams[propertyPath] = `[${(value as string[]).map((e) => `'${e}'`).join(', ')}]`
      } else {
        explode(value as BidParams, propertyPath)
      }
    }
  }
  explode(bidderParamsValue ?? {})
  return explodedParams
}

export const generateCSVHeaders = (bidderSeat: BidderSeat, hasPlacementDimension: boolean): string[] => {
  // Filter and sort dimension keys to match the order in CSV rows
  // Order: siteId → device → placementId/adUnitId → mediaType
  const dimensionOrder: MappingDimensionKey[] = ['siteId', 'device', 'placementId', 'adUnitId', 'mediaType']
  const dimensionKeys = dimensionOrder.filter((dimKey) => {
    // Exclude dimensions not in bidderSeat.mappingDimensions
    if (!bidderSeat.mappingDimensions.includes(dimKey)) return false
    // If placementId is present, exclude adUnitId
    if (hasPlacementDimension && dimKey === 'adUnitId') return false
    return true
  })
  return dimensionKeys.map((dimKey) => MAPPING_DIMENSIONS[dimKey])
}
export const FormattedBidParamsSchema: z.ZodType<any> = z.lazy(() => z.record(z.string(), z.union([z.number(), z.string(), z.boolean(), z.array(z.string()), FormattedBidParamsSchema])))
